import {
  DEFAULT_SUFFICIENCY_THRESHOLDS,
  type SufficiencyThresholds,
} from '~/calculations/weightTrends'
import type { PhaseType } from '~/models/phase'

/**
 * The single home for every numeric threshold the engine uses.
 *
 * No numeric literal appears inside a ruleset (implementation-plan.md §2, rule
 * 5). Each value below carries the decision that authorised it, so a reader can
 * tell approved guidance from an implementation detail.
 *
 * **B-004 deliberately does NOT live here**, though an earlier note said it
 * would. Its output is a set of *suggested defaults* for a phase record, not a
 * threshold the engine reads: every phase stores its own target range, and the
 * engine judges a week against that stored value. The defaults therefore sit
 * with the phase model, in `models/phase.ts`, and are recorded in D-065.
 *
 * **B-005 is closed (D-066) and its values are here — but most are not read
 * yet.** Adherence, recovery and strength are not collected until Checkpoint 2,
 * and D-060 forbids implementing a rule whose evidence the app does not gather.
 * The approved numbers are recorded now so the rulesets written at Checkpoint 2
 * consume an approved value rather than inventing one mid-implementation. The
 * reduced fat-loss ruleset needs none of them — see `rulesets/fatLoss.ts`.
 */
export interface RulesThresholds {
  /** Weigh-in counts that make a week judgeable (D-036). */
  readonly sufficiency: SufficiencyThresholds
  /**
   * Tolerance when comparing a weekly rate against the phase's configured
   * range, as percentage points per week.
   *
   * Not coaching guidance: it exists because a rate of exactly the configured
   * boundary must count as inside the range, and binary floating point cannot
   * represent decimals like 0.4 exactly. The same reasoning as the 1e-9 epsilon
   * in measurement spread comparison (D-054), scaled to percentage points.
   */
  readonly rateComparisonEpsilonPercent: number
  /**
   * Below this absolute weekly change, weight is treated as flat rather than
   * moving, in percent of body weight per week.
   *
   * Derived, not invented: it is the smallest weekly change the app can
   * distinguish from ordinary day-to-day variation given a seven-day average.
   * It exists so "stable weight with a falling waist" (rules-engine.md, fat
   * loss) can be recognised at all. Reviewed with B-005.
   */
  readonly flatWeightBandPercent: number
  /**
   * Minimum fall in waist, in centimetres between consecutive measurement
   * sessions, treated as a real reduction rather than measurement noise.
   *
   * Anchored to the approved measurement protocol rather than chosen: D-016
   * warns when three readings of one site spread by more than 0.7 cm, so a
   * change smaller than that spread cannot be distinguished from the noise the
   * protocol already tolerates.
   */
  readonly waistFallMinCm: number
  /** Approved adherence tiers (D-066, closing part of B-005). */
  readonly adherence: AdherenceThresholds
  /** Approved recovery-deterioration trigger (D-066). */
  readonly recovery: RecoveryThresholds
  /** Approved strength-decline trigger (D-066). */
  readonly strength: StrengthThresholds
  /** Consecutive weeks a pattern must hold before confidence may reach `high` (D-066). */
  readonly consecutiveWeeksForHighConfidence: number
}

/**
 * Two tiers, because they answer two different questions.
 *
 * `toJudge` is whether the plan was followed closely enough to conclude
 * anything about it; below it the week returns "improve adherence first" and
 * D-007's hard filter in `prioritize.ts` refuses any intake cut. `toChange` is
 * the higher bar required before proposing an adjustment at all, and it is what
 * the fat-loss plateau rule means by "high adherence".
 */
export interface AdherenceThresholds {
  readonly nutritionDaysOnPlanToJudge: number
  readonly nutritionDaysOnPlanToChange: number
  /** Fraction of *planned* sessions completed, so a 3-day and a 6-day week compare fairly. */
  readonly plannedSessionFractionToJudge: number
  /** Sessions that may be missed at the higher tier, as a count rather than a fraction. */
  readonly plannedSessionsMissedAllowedToChange: number
}

/**
 * Recovery ratings run 1–5 with **5 always the better outcome**, including the
 * items whose plain reading is inverted: "hunger control" rather than "hunger",
 * "calm" rather than "stress" (D-066). Without that, one threshold could not
 * apply across all five.
 *
 * Two triggers rather than one, because a single item collapsing and a general
 * slide are different failure modes and averaging hides the first — the same
 * reasoning that keeps measurement quality per site rather than per session
 * (D-046).
 */
export interface RecoveryThresholds {
  readonly singleRatingConcernAtOrBelow: number
  readonly meanDropBelowFourWeekAverage: number
  readonly scaleMin: number
  readonly scaleMax: number
}

/**
 * The size, breadth and duration of a strength decline (D-066), expressed
 * against the estimated 1RM that **D-069** defines (closing L-002): Epley,
 * `load × (1 + reps / 30)`, published as `estimated_1rm_epley_v1`.
 *
 * Two lifts and two consecutive weeks together filter the noise a single bad
 * session produces. The rep cap does the rest: D-066 rejected a 3% trigger
 * because estimates drawn from different rep ranges are noisy, and limiting
 * which sets may produce an estimate is the direct remedy for that rather than
 * a second guess at it.
 *
 * Because the rule compares a lift against **its own** trailing average, the
 * formula choice moves the absolute number a user reads far more than the trend
 * this threshold fires on.
 */
export interface StrengthThresholds {
  readonly declinePercentBelowFourWeekAverage: number
  readonly liftsRequired: number
  readonly consecutiveWeeksRequired: number
  /**
   * Sets above this rep count do not produce an estimated 1RM (D-069).
   *
   * Every rep-based formula degrades as reps rise — Epley inflates, and Brzycki
   * is undefined at 37 reps and unusable well before it. Such sets are still
   * stored; they are excluded from the estimate with a reason, never silently
   * dropped, and a week with no qualifying set has no estimate at all rather
   * than a zero.
   */
  readonly maxRepsCountedForEstimated1RM: number
}

export const DEFAULT_RULES_THRESHOLDS: RulesThresholds = {
  sufficiency: DEFAULT_SUFFICIENCY_THRESHOLDS,
  rateComparisonEpsilonPercent: 1e-9,
  flatWeightBandPercent: 0.1,
  waistFallMinCm: 0.7,
  adherence: {
    nutritionDaysOnPlanToJudge: 5,
    nutritionDaysOnPlanToChange: 6,
    plannedSessionFractionToJudge: 2 / 3,
    plannedSessionsMissedAllowedToChange: 1,
  },
  recovery: {
    singleRatingConcernAtOrBelow: 2,
    meanDropBelowFourWeekAverage: 1,
    scaleMin: 1,
    scaleMax: 5,
  },
  strength: {
    declinePercentBelowFourWeekAverage: 5,
    liftsRequired: 2,
    consecutiveWeeksRequired: 2,
    maxRepsCountedForEstimated1RM: 10,
  },
  consecutiveWeeksForHighConfidence: 2,
}

/**
 * Route-recommendation thresholds (D-071, closing L-012).
 *
 * **Provenance is not uniform, and it is recorded per value below** because an
 * earlier draft of D-071 attributed two invented numbers to published guidance.
 * Only `fatLossPriorityWaistToHeight` has a citable source. Nothing in the code,
 * the documents or the handoff interpreted waist-to-height before this.
 *
 * **These are routing boundaries, not health verdicts.** Published
 * waist-to-height bands are cardiometabolic risk-screening thresholds and were
 * not derived to answer "surplus or deficit". The repurposing is deliberate and
 * bounded: no output states or implies a risk category, and D-015's rule that
 * every body-composition value is an estimate rather than a diagnosis stands.
 *
 * **Chosen safe for a stranger, not tuned to the current user**, because the
 * user intends to let friends install the app. Safety runs in opposite
 * directions for the two ratios, which is why the band between them is narrow —
 * see the risk note in decisions.md.
 *
 * Nothing reads these yet: the route engine is Checkpoint 2.5 and D-060 forbids
 * a rule whose evidence the app does not gather. They are recorded now so the
 * engine consumes an approved value rather than inventing one.
 */
export interface RouteThresholds {
  /**
   * At or above this waist-to-height ratio, fat loss takes priority over a
   * stated muscle-building goal.
   *
   * **PUBLISHED**: the NICE / Ashwell "keep your waist under half your height"
   * boundary. The only value in this interface with a citable source.
   */
  readonly fatLossPriorityWaistToHeight: number
  /**
   * Below this waist-to-height ratio, fat loss is no longer recommended:
   * "visible abs" becomes maintenance, "look better in clothes" a lean bulk.
   *
   * **Coaching convention, no published source.** Chosen in the conservative
   * direction — erring high means telling someone to stop cutting sooner than
   * they wanted, which is milder than endorsing continued fat loss into
   * genuinely lean territory for a person the app cannot see.
   */
  readonly leanEnoughWaistToHeight: number
  /**
   * Minimum realistic weekly sessions before a lean bulk may be recommended.
   * Below it, "build muscle" returns maintenance **with the frequency named as
   * the reason** rather than a silent substitution.
   *
   * **Coaching convention, no published source.**
   */
  readonly minWeeklySessionsForLeanBulk: number
  /**
   * Minimum weeks to an event before an aesthetic preparation is recommended.
   * Below it every band returns maintenance, because the alternative invites the
   * crash behaviour phase-system.md forbids the app to encourage.
   *
   * **Coaching convention**, derived from the approved −0.5%/−0.3% per week
   * range (D-065): eight weeks is roughly the least that produces a visible
   * change at that rate without pressure to exceed it.
   */
  readonly minWeeksForAestheticPrep: number
}

export const DEFAULT_ROUTE_THRESHOLDS: RouteThresholds = {
  fatLossPriorityWaistToHeight: 0.5,
  leanEnoughWaistToHeight: 0.45,
  minWeeklySessionsForLeanBulk: 3,
  minWeeksForAestheticPrep: 8,
}

/**
 * What the route engine reads, in one value.
 *
 * **No new number is introduced here.** It composes the D-071 boundaries with
 * the measurement-noise figure already approved above, which the engine uses to
 * decide when a reading sits too close to a boundary to be confident about the
 * side it falls on. Reusing that figure rather than picking a second one keeps
 * "how precisely can this app know a waist?" answered in exactly one place.
 */
export interface RouteEngineThresholds {
  readonly route: RouteThresholds
  /** Smallest waist change distinguishable from noise, in centimetres. */
  readonly waistNoiseCm: number
}

export const DEFAULT_ROUTE_ENGINE_THRESHOLDS: RouteEngineThresholds = {
  route: DEFAULT_ROUTE_THRESHOLDS,
  waistNoiseCm: DEFAULT_RULES_THRESHOLDS.waistFallMinCm,
}

/**
 * A mini cut is **never** recommended as a first project (D-071).
 *
 * At −1.0% to −0.7% per week it is the most aggressive thing the engine can
 * prescribe, and a first-day snapshot lacks exactly the adherence, recovery and
 * strength history needed to judge whether someone can tolerate it. The phase
 * itself is unaffected — it stays implementable and choosable for a later
 * project. Only the setup route engine refuses to emit it.
 */
export const ROUTES_NEVER_RECOMMENDED_AT_SETUP: readonly PhaseType[] = ['miniCut']

/**
 * Rule precedence (D-066, closing the last part of B-005).
 *
 * **Ordering, not weights.** When several families fire, the first in this list
 * supplies the primary action and the rest are recorded as rejected
 * alternatives. The order is the evaluation order already approved in
 * rules-engine.md — safety and recovery can outrank ordinary adherence logic,
 * and both outrank ordinary phase progress — so precedence became structure
 * rather than a new set of numbers nobody had approved.
 */
/**
 * `nutritionSourceConflict` sits third (D-096), and the position is argued rather
 * than convenient.
 *
 * It is **below safety and adherence**, because a disagreement about a target rate
 * is never more urgent than falling recovery or a plan that was not followed. It is
 * **above phase progress**, because when the system that actually sets the numbers
 * is configured for a different rate, "you are slower than your range" is measuring
 * against a rate the user's real plan was never set to — reporting that verdict
 * first would be answering with the less true of the two facts.
 *
 * It fires every week until the user reconciles the two, and that is intended:
 * `phasePlannedEndReached` already stands unchanged week after week for the same
 * reason. A notice that changes nothing is not a nag when the thing it names has
 * not changed either.
 */
export const RULE_FAMILY_PRECEDENCE = [
  'safetyAndRecovery',
  'adherence',
  'nutritionSourceConflict',
  'phaseProgress',
] as const

export type RuleFamily = (typeof RULE_FAMILY_PRECEDENCE)[number]

/**
 * Tie-break within one family, highest priority first.
 *
 * The approved evidence hierarchy for body-composition direction
 * (rules-engine.md, D-005). A waist-based candidate beats a weight-based one;
 * neither is beaten by the circumference estimate, which sits last precisely
 * because it is derived from the measurements above it.
 */
export const EVIDENCE_PRECEDENCE = [
  'waist',
  'weight',
  'strength',
  'photos',
  'navyEstimate',
] as const

export type EvidenceKind = (typeof EVIDENCE_PRECEDENCE)[number]
